import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { getGameStatus } from '../gameSlice';
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { gsap, Elastic } from "gsap/all";
import useDisableBrowserBack from "../../hooks/useDisableBrowserBack";
import useReadCookies from "../../hooks/useReadCookies";
import Help from "../shared/help";
import Timer from "../shared/timer";
import ClassScore from "./classScore";
import Cookies from "js-cookie";
import api from '../../../api';
import StarSvg from "./starSvg";
import HelpContent from "../shared/helpContent";

function GameOverview() {
  useDisableBrowserBack();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [time, setTime] = useState(0);
  const [showHelp, setShowHelp] = useState(false);
  const [showScoreTootip, setShowScoreTooltip] = useState(false);
  const [startButtonPositionY, setStartButtonPositionY] = useState(null);
  const [startButtonVisible, setStartButtonVisible] = useState(false);
  const [nextMissionStarted, setNextMissionStarted] = useState(false);

  const gameStarted = useSelector((state) => state.game.gameStarted);
  const timeLimit = useSelector((state) => state.game.timeLimit);

  const scrollContainer = useRef(null);
  const nextMissionRef = useRef(null);

  const missions = useSelector((state) => state.game.missions);
  const currentMission = useSelector((state) => state.game.currentMission);

  let { cookiePlayerId, cookieGroupId } = useReadCookies();

  const handleScroll = (e) => {
    updateStartButtonPosition();
  };

  const handleResize = (e) => {
    updateStartButtonPosition();
  }

  useEffect(() => {
    api.createQuestions(cookiePlayerId).then(result => {
      if (result.status === 200) {
        dispatch(getGameStatus(cookieGroupId));
      }
    });
    if (!showScoreTootip) {
      setTimeout(() => {
        setShowScoreTooltip(true);
        setTimeout(() => {
          setShowScoreTooltip(false);
        }, 5000);
      }, 3000);
    }
    // polling for group status every 5 seconds until group is confirmed  
    const interval = setInterval(() => {
      dispatch(getGameStatus(cookieGroupId));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (nextMissionRef.current) {
      updateStartButtonPosition();
    }
  }, [nextMissionRef.current]);

  useEffect(() => {
    const timer = setInterval(() => {
      let startTime = new Date(gameStarted);
      let currentTime = new Date();
      let timeDiff = Math.round((currentTime - startTime) / (1000 * 60));
      setTime(timeLimit - timeDiff);
    }, 1000);
    return () => clearInterval(timer);
  }, [gameStarted]);

  useEffect(() => {
    // TODO, 
  }, [currentMission])

  // Scroll to current selection
  useEffect(() => {
    const delay = setTimeout(() => {
      scrollToCurrentMission();
    }, 1500);

    window.addEventListener('resize', handleResize, false);

    return () => {
      clearTimeout(delay);
      window.removeEventListener('resize', handleResize);
    };
  }, [currentMission]);

  useEffect(() => {
    if (missions.length > 0 && missions[currentMission].started && !missions[currentMission].finished) {
      setNextMissionStarted(true);
      setTimeout(() => {
        navigate(`/app/game/questions${window.location.search}`);
      }, 3000);
    }
  }, [missions, currentMission]);

  const calculateCurrentSelectionOffset = () => {
    const mobileContainerOfset = document.querySelector('.mobileContainer').getBoundingClientRect();
    const activeMissionIndicatorOffset = nextMissionRef.current.getBoundingClientRect();
    const correctedOffset = Math.round(activeMissionIndicatorOffset.y - mobileContainerOfset.y);
    return correctedOffset;
  }
  const updateStartButtonPosition = () => {
    const offset = calculateCurrentSelectionOffset();
    if (offset > 150) {
      setStartButtonVisible(true);
      setStartButtonPositionY(offset);
    } else {
      setStartButtonVisible(false);
    }
  }

  const scrollToCurrentMission = () => {
    const offset = calculateCurrentSelectionOffset();
    let newScrollPos = (scrollContainer.current.scrollTop + offset) - (scrollContainer.current.getBoundingClientRect().height / 2);
    if (currentMission < 1) {
      newScrollPos = 0;
    }
    scrollContainer.current.scrollTo(0, newScrollPos);
  }

  const handleStartMission = () => {

    api.startMission(cookieGroupId).then(result => {

      if (result.status === 200) {
        dispatch(getGameStatus(cookieGroupId));
        setNextMissionStarted(true);
      }
    });
  }

  const getMissionRef = (mission, index) => {
    if (index === 0 && currentMission === 0 && !missions[0].finished) {
      return nextMissionRef;
    } else if (missions[0].finished && currentMission + 1 === mission.mission_number && !mission.finished) {
      return nextMissionRef;
    } else
      return null;
  }

  return (
    <div className="mobileContainer backgroundPatternDark game-overview-screen ">
      {nextMissionStarted &&
        <div className={`loader ${nextMissionStarted ? 'active' : ''}`}></div>
      }

      <div className="appHeader">
        <Timer tooltip={currentMission === 0 && !missions[currentMission].finished ? true : false} />
        <Help setShowHelp={setShowHelp} />
      </div>

      <ClassScore />

      <div className="missionOverviewContainer">
        <div className="missionOverviewHeader">
          <div className="ornament"></div>
          <h1 className="headerTitle">{t("student.game_overview.title")}</h1>
        </div>
        <div className="missionOverviewScroll" onScroll={handleScroll} ref={scrollContainer}>
          <div className="missionOverview">
            {currentMission === 0 && !missions[currentMission].finished &&
              <div className="introText">
                <p>
                  {t("student.game_overview.intro_text", [time])}
                </p>
              </div>
            }
            <div className={`missionTimelineContainer ${currentMission === 0 && !missions[currentMission].finished ? '' : 'extraSpacing'}`}>
              <ul className="missionTimeline">
                {missions && missions.map((mission, index) => {
                  let prevMission = index > 0 ? missions[index - 1] : null;
                  return (
                    <li
                      key={index}
                      ref={getMissionRef(mission, index)}
                      className={`missionItem 
                      ${mission.mission_number === 0 && (!mission.started || !mission.finished) ? 'active' : ''}
                      ${prevMission && prevMission.finished && (!mission.started || !mission.finished) ? 'active' : ''}
                      ${mission.finished ? 'completed' : ''}
                      ${mission.finished && !mission.mission_passed ? 'fault' : ''}
                      `}>
                      <div className="focusRingsContainer">
                        <div className="focusRingSimple"></div>
                        <div className="focusRingSimple"></div>
                      </div>
                      <div className="missionItemEmblem" >
                        <div className="number">{mission.mission_number + 1}</div>
                      </div>
                      {mission.finished &&
                        <ul className="collectable-container">
                          {mission.finished && mission.mission_passed &&
                            <li className="collectable"></li>
                          }
                          {mission.finished && mission.mix_n_match_finished &&
                            <li className="collectable"></li>
                          }
                          {mission.finished && mission.swipe_finished &&
                            <li className="collectable"></li>
                          }
                        </ul>
                      }
                    </li>
                  )
                })}
              </ul>
            </div>
            <div
              className={`startButtonContainer ${startButtonVisible ? 'buttonVisible' : ''}`}
              style={
                startButtonPositionY
                  ? { transform: `translateY(${startButtonPositionY}px)` } : {}
              }
            >
              <div className="startButton">
                <div className="startButtonFrame" onClick={handleStartMission}>
                  <div className="startButtonCaption">{t("student.game_overview.start_button")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showHelp && <HelpContent setShowHelp={setShowHelp} page={'game_overview'} />}
    </div>
  );
}
/*

<div className="header-section" ref={e => {headerRef = e}}>
        <Help setShowHelp={setShowHelp} />
        <h3 className="title">
          { t("student.game_overview.title")}
        </h3>
        <Timer tooltip={currentMission === 0 && !missions[currentMission].finished ? true : false} />
      </div>
      <div className="body-section">
        <div className="game-info" ref={e => { gameInfoRef = e}}>
          {currentMission === 0 && !missions[currentMission].finished &&
            <div className="intro-text">
              <p>
                { t("student.game_overview.intro_text", [time])}
              </p>
            </div>
          }
          { ((currentMission === 0 && missions[currentMission].finished) || currentMission > 0) &&
            <div className="your-team-title">
              <h3>
                { t("student.game_overview.your_team")}
              </h3>
            </div>
          }
          <div className="game-sequence" style={{ marginTop: marginTop }} ref={ e => { gameSequenceRef = e }}>
            <ul>
              {missions && missions.map((mission, index) => {
                return (
                  <li key={index}>
                    <div className={`game-item-container 
                        ${currentMission === mission.mission_number && !mission.finished ? 'start' : ''}
                        ${mission.finished ? 'completed' : ''}
                        ${currentMission !== 0 && currentMission + 1 === mission.mission_number && !mission.finished ? 'next' : ''}
                        `}
                        ref={ getMissionRef(mission, index)}
                        >
                      <div className="game-item-number">
                        <div>
                          <p>{mission.mission_number + 1}</p>
                        </div>
                      </div>
                      <div className={`main-question ${mission.finished && mission.mission_passed ? 'completed' : ''}`}>
                        { mission.finished && mission.mission_passed &&
                          <StarSvg />
                        }
                      </div>
                      <div className={`mini-game ${mission.finished && mission.mix_n_match_finished ? 'completed' : ''}`}>
                        { mission.finished && mission.mix_n_match_finished && 
                          <StarSvg />
                        }
                      </div>
                      <div className={`mini-game ${mission.finished && mission.swipe_finished ? 'completed' : ''}`}>
                        { mission.finished && mission.swipe_finished && 
                          <StarSvg />
                        }
                      </div>
                      </div>
                      </li>
                    )
                  })}
                </ul>
              </div>
            </div>
            <div className="class-summary" ref={e => { classSummaryRef = e }}>
              <h4 className="class-summary-title">
                { t("student.game_overview.class_score_title")}
              </h4>
              { currentMission === 0 && !missions[currentMission].finished && showScoreTootip &&
                <div className="score-tooltip">
                  <span>{ t("student.game_overview.score_tooltip") }</span>
                </div>
              }
              <ClassScore />
            </div>
          </div>
          <div className="mission-start-button" onClick={handleStartMission} style={{ 'top': startButtonTop }} ref={ e => { startButtonRef = e }}>
            <p>{t("student.game_overview.start_button")}</p>
          </div>
          {showHelp && <HelpContent setShowHelp={setShowHelp} page={'game_overview'} />}

*/
export default GameOverview;